import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, User, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { VisitService } from '@/lib/visit-service';
import { getFaceImageUrl } from '@/lib/face-utils';

const FACE_PAGE_SIZE = 20;
const UNKNOWN_CUSTOMER_RETENTION_DAYS = 7;
const UNKNOWN_CUSTOMER_RETENTION_MS = UNKNOWN_CUSTOMER_RETENTION_DAYS * 24 * 60 * 60 * 1000;

interface Face {
  id: string;
  company_id: string;
  created_at: string;
  visit_id: string;
  customer_id: string | null;
  quality_score: number | null;
  src_image_path: string | null;
  meta: unknown;
}

interface AddFaceRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  companyId: string;
  onSaved?: () => void;
}

const getExpiryTimeMs = (createdAt: string): number => {
  return new Date(createdAt).getTime() + UNKNOWN_CUSTOMER_RETENTION_MS;
};

export function AddFaceRecordModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  companyId,
  onSaved,
}: AddFaceRecordModalProps) {
  const { toast } = useToast();
  const [faces, setFaces] = useState<Face[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFace, setSelectedFace] = useState<Face | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [visibleFaceCount, setVisibleFaceCount] = useState(FACE_PAGE_SIZE);

  const fetchUnknownFaces = async (options?: { preserveVisibleCount?: boolean }) => {
    if (!companyId) return;

    try {
      const { data: unknownVisits, error: visitsErr } = await (supabase as any)
        .from('visits')
        .select('id, company_id, customer_id, entered_at, created_at, status')
        .eq('company_id', companyId)
        .is('customer_id', null)
        .eq('status', 'PENDING_LABEL')
        .order('entered_at', { ascending: false })
        .limit(100);

      if (visitsErr || !unknownVisits?.length) {
        setFaces([]);
        return;
      }

      const visitIds = unknownVisits.map((v: any) => v.id);
      const visitMetaById = new Map(unknownVisits.map((v: any) => [v.id, v]));

      const { data: visitFaceRows, error: visitFacesErr } = await (supabase as any)
        .from('visit_faces')
        .select('id, visit_id, snapshot_path, quality_score, created_at')
        .in('visit_id', visitIds)
        .order('created_at', { ascending: false });

      if (visitFacesErr) {
        setFaces([]);
        return;
      }

      const entryRows: any[] = [];
      for (const row of visitFaceRows || []) {
        entryRows.push(row);
      }

      const mappedFaces: Face[] = entryRows.map((row: any) => {
        const visitMeta = visitMetaById.get(row.visit_id);
        return {
          id: row.id,
          company_id: visitMeta?.company_id ?? companyId,
          created_at: row.created_at,
          visit_id: row.visit_id,
          customer_id: null,
          quality_score: row.quality_score,
          src_image_path: row.snapshot_path ?? null,
          meta: null,
        };
      });

      const visitIdsWithFace = new Set(entryRows.map((r: any) => r.visit_id));
      const syntheticFaces: Face[] = unknownVisits
        .filter((v: any) => !visitIdsWithFace.has(v.id))
        .map((v: any) => ({
          id: `visit-${v.id}`,
          company_id: v.company_id ?? companyId,
          created_at: v.entered_at || v.created_at || new Date().toISOString(),
          visit_id: v.id,
          customer_id: null,
          quality_score: null,
          src_image_path: null,
          meta: { synthetic: true },
        }));

      const combined = [...mappedFaces, ...syntheticFaces].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setFaces(combined);
      setVisibleFaceCount((prev) =>
        options?.preserveVisibleCount ? Math.max(prev, FACE_PAGE_SIZE) : FACE_PAGE_SIZE
      );
    } catch (err) {
      console.error('[AddFaceRecordModal] fetch error:', err);
      toast({
        title: 'Hata',
        description: 'Bilinmeyen yüzler yüklenirken hata oluştu',
        variant: 'destructive',
      });
    }
  };

  const loadImageUrls = async (facesList: Face[]) => {
    const toLoad = facesList.filter((f) => f.src_image_path && !imageUrls[f.id]);
    if (toLoad.length === 0) return;

    const entries = await Promise.all(
      toLoad.map(async (face) => {
        const url = await getFaceImageUrl(face.src_image_path);
        return url ? ([face.id, url] as const) : null;
      })
    );

    const next: Record<string, string> = {};
    entries.forEach((e) => {
      if (e) next[e[0]] = e[1];
    });
    if (Object.keys(next).length > 0) {
      setImageUrls((prev) => ({ ...prev, ...next }));
    }
  };

  useEffect(() => {
    if (isOpen && companyId) {
      setLoading(true);
      setSelectedFace(null);
      setVisibleFaceCount(FACE_PAGE_SIZE);
      fetchUnknownFaces().finally(() => setLoading(false));
    }
  }, [isOpen, companyId]);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  const activeFaces = useMemo(
    () => faces.filter((f) => getExpiryTimeMs(f.created_at) > nowMs),
    [faces, nowMs]
  );
  const displayedFaces = useMemo(
    () => activeFaces.slice(0, visibleFaceCount),
    [activeFaces, visibleFaceCount]
  );
  const hasMoreFaces = displayedFaces.length < activeFaces.length;

  const handleLoadMoreFaces = () => {
    setVisibleFaceCount((prev) => prev + FACE_PAGE_SIZE);
  };

  useEffect(() => {
    if (displayedFaces.length > 0) {
      loadImageUrls(displayedFaces);
    }
  }, [displayedFaces]);

  const handleSave = async () => {
    if (!selectedFace) return;

    setSaving(true);
    try {
      await VisitService.linkVisitToCustomer(selectedFace.visit_id, customerId);

      toast({
        title: 'Başarılı',
        description: 'Yüz kaydı müşteriye eşleştirildi',
      });

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('[AddFaceRecordModal] save error:', err);
      toast({
        title: 'Hata',
        description: 'Yüz eşleştirmesi yapılırken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Yüz Kaydı Ekle
          </DialogTitle>
          <DialogDescription>
            <strong>{customerName}</strong> müşterisinin veritabanında yüz kaydı bulunmuyor. Bilinmeyen
            yüzlerden birini seçerek bu müşteriye eşleştirebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : activeFaces.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Eşleştirilebilecek bilinmeyen yüz bulunamadı.</p>
              <p className="text-sm mt-2">Daha sonra Bilinmeyen Müşteriler sayfasından ekleyebilirsiniz.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {displayedFaces.map((face) => {
                  const isSelected = selectedFace?.id === face.id;
                  return (
                    <button
                      key={face.id}
                      type="button"
                      onClick={() => setSelectedFace(face)}
                      className={`relative aspect-square rounded-lg overflow-hidden transition-all ${
                        isSelected
                          ? 'border-4 border-green-500 ring-4 ring-green-400/60 ring-offset-2 ring-offset-background'
                          : 'border-2 border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      {face.src_image_path ? (
                        imageUrls[face.id] ? (
                          <img
                            src={imageUrls[face.id]}
                            alt="Yüz"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0YzRjRGNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkeT0iLjM4ZW0iIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5Q0EzQUYiPuKAizwvdGV4dD48L3N2Zz4=';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                          </div>
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <User className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-green-600/50">
                          <div className="rounded-full bg-green-600 p-2 shadow-lg">
                            <Check className="w-6 h-6 text-white" strokeWidth={3} />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {hasMoreFaces && (
                <div className="flex justify-center pt-4">
                  <Button onClick={handleLoadMoreFaces} variant="outline">
                    Daha Fazla Yükle ({FACE_PAGE_SIZE})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-4">
          <Button variant="outline" onClick={handleSkip}>
            <X className="w-4 h-4 mr-2" />
            Daha Sonra
          </Button>
          <Button onClick={handleSave} disabled={!selectedFace || saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
